import { LoaderCircle } from 'lucide-react';
import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx, focusRingClass, interactiveClass } from './shared';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'guarded'
  | 'caution'
  | 'danger'
  | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  /**
   * Why the button can't be used right now. Shown under the button while it
   * is disabled (not while it is loading) and linked with aria-describedby.
   */
  disabledReason?: ReactNode;
  /** Layout classes for the button-and-reason wrapper (e.g. `w-full`). */
  reasonWrapperClassName?: string;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border-accent bg-accent text-on-accent hover:border-accent-hover hover:bg-accent-hover',
  secondary:
    'border-border-strong bg-surface text-ink hover:border-accent hover:bg-accent-soft hover:text-accent-ink',
  guarded:
    'border-accent bg-accent-soft text-accent-ink hover:border-accent-hover hover:bg-surface',
  caution:
    'border-status-blocked-border bg-status-blocked-surface text-status-blocked hover:border-status-blocked hover:bg-surface',
  danger:
    'border-action-danger bg-action-danger text-on-danger hover:opacity-90',
  ghost:
    'border-transparent bg-transparent text-ink hover:border-border-subtle hover:bg-surface-recessed',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-control-comfortable px-3 text-control',
  md: 'min-h-control-comfortable px-4 text-control',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      className,
      children,
      disabled,
      leftIcon,
      type = 'button',
      disabledReason,
      reasonWrapperClassName,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const reasonId = useId();
    const showReason = Boolean(disabled && !isLoading && disabledReason);
    const describedBy = cx(ariaDescribedBy, showReason && reasonId) || undefined;
    const button = (
      <button
        ref={ref}
        type={type}
        className={cx(
          focusRingClass,
          interactiveClass,
          'inline-flex max-w-full items-center justify-center gap-inline-control-gap rounded-control border font-semibold disabled:cursor-not-allowed disabled:border-border-strong disabled:bg-surface-recessed disabled:text-ink-muted',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        aria-describedby={describedBy}
        {...rest}
      >
        {isLoading ? (
          <LoaderCircle className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        ) : leftIcon ? (
          <span className="shrink-0" aria-hidden="true">
            {leftIcon}
          </span>
        ) : null}
        <span>{children}</span>
      </button>
    );
    if (!showReason) return button;
    return (
      <span
        className={cx(
          'inline-flex max-w-full flex-col items-start gap-1',
          reasonWrapperClassName,
        )}
      >
        {button}
        <span id={reasonId} className="max-w-reading text-meta text-ink-muted">
          {disabledReason}
        </span>
      </span>
    );
  },
);

Button.displayName = 'Button';

export default Button;
